import React, { useState, useEffect } from 'react';
import { List, Card, Tag, Checkbox, Input, Typography } from 'antd';
import { DragDropContext, Droppable, Draggable } from '@hello-pangea/dnd';
import { SearchOutlined, DragOutlined } from '@ant-design/icons';

const { Title } = Typography;

const AssetSelector = ({ availableAssets, selectedAssets, onAssetsChange }) => {
    const [available, setAvailable] = useState(availableAssets);
    const [selected, setSelected] = useState(selectedAssets);
    const [availableSearchTerm, setAvailableSearchTerm] = useState('');
    const [selectedSearchTerm, setSelectedSearchTerm] = useState('');

    useEffect(() => {
        setAvailable(availableAssets);
        setSelected(selectedAssets);
    }, [availableAssets, selectedAssets]);

    const handleCheckboxChange = (asset, checked) => {
        if (checked) {
            const newSelected = [...selected, asset];
            setSelected(newSelected);
            onAssetsChange(newSelected);
        } else {
            const newSelected = selected.filter(item => item.id !== asset.id);
            setSelected(newSelected);
            onAssetsChange(newSelected);
        }
    };

    const onDragEnd = (result) => {
        if (!result.destination) return;
    
        const items = Array.from(selected);
        const [reorderedItem] = items.splice(result.source.index, 1);
        items.splice(result.destination.index, 0, reorderedItem);
    
        // 更新排序：新增 `order` 欄位
        const reorderedItems = items.map((item, index) => ({
            ...item,
            order: index, // 根據順序新增 order
        }));
    
        setSelected(reorderedItems);
        onAssetsChange(reorderedItems); // 更新父元件
    };

    const renderAssetCard = (item) => (
        <Card size="small" className="asset-card">
            <div className="asset-info">
                <span className="asset-id">{item.id}</span>
                <span className="asset-name">{item.name}</span>
                <Tag color="blue" className="asset-type">{item.type}</Tag>
            </div>
        </Card>
    );

    // 左邊過濾後的可用清單
    const filteredAvailableAssets = available.filter(asset =>
        asset.name.toLowerCase().includes(availableSearchTerm.toLowerCase()) ||
        asset.type.toLowerCase().includes(availableSearchTerm.toLowerCase())
    );

    // 右邊過濾後的已選擇清單
    const filteredSelectedAssets = selected.filter(asset =>
        asset.name.toLowerCase().includes(selectedSearchTerm.toLowerCase()) ||
        asset.type.toLowerCase().includes(selectedSearchTerm.toLowerCase())
    );

    const renderAvailableList = () => (
        <List
            size="small"
            dataSource={filteredAvailableAssets}
            renderItem={(item) => (
                <List.Item
                    className="asset-item"
                    onClick={() => handleCheckboxChange(item, !selected.some(asset => asset.id === item.id))}
                    style={{ cursor: 'pointer' }}
                >
                    <Checkbox
                        checked={selected.some(asset => asset.id === item.id)}
                        onChange={(e) => handleCheckboxChange(item, e.target.checked)}
                        style={{ marginRight: '12px' }}
                        onClick={(e) => e.stopPropagation()}
                    />
                    {renderAssetCard(item)}
                </List.Item>
            )}
        />
    );

    const renderSelectedList = () => (
        <DragDropContext onDragEnd={onDragEnd}>
            <Droppable droppableId="selected">
                {(provided) => (
                    <List
                        size="small"
                        {...provided.droppableProps}
                        ref={provided.innerRef}
                        dataSource={filteredSelectedAssets}
                        renderItem={(item, index) => (
                            <Draggable key={item.id.toString()} draggableId={item.id.toString()} index={index}>
                                {(provided) => (
                                    <List.Item
                                        ref={provided.innerRef}
                                        {...provided.draggableProps}
                                        {...provided.dragHandleProps}
                                        className="asset-item draggable"
                                    >
                                        <DragOutlined className="drag-handle" />
                                        {renderAssetCard(item)}
                                    </List.Item>
                                )}
                            </Draggable>
                        )}
                    >
                        {provided.placeholder}
                    </List>
                )}
            </Droppable>
        </DragDropContext>
    );

    return (
        <div className="asset-selector">
            {/* 左側 Available Assets */}
            <div className="asset-column available-assets">
                <Title level={5}>Available Assets (Count: {filteredAvailableAssets.length})</Title>
                <Input
                    placeholder="Search available assets..."
                    prefix={<SearchOutlined />}
                    onChange={(e) => setAvailableSearchTerm(e.target.value)}
                    className="asset-search"
                />
                <div className="asset-list">{renderAvailableList()}</div>
            </div>

            {/* 右側 Selected Assets */}
            <div className="asset-column selected-assets">
                <Title level={5}>Selected Assets (Count: {filteredSelectedAssets.length})</Title>
                <Input
                    placeholder="Search selected assets..."
                    prefix={<SearchOutlined />}
                    onChange={(e) => setSelectedSearchTerm(e.target.value)}
                    className="asset-search"
                />
                <div className="asset-list">{renderSelectedList()}</div>
            </div>

            <style jsx>{`
                .asset-selector {
                    display: flex;
                    gap: 16px;
                    width: 100%;
                }
                .asset-column {
                    flex: 1;
                    display: flex;
                    flex-direction: column;
                }
                .asset-search {
                    margin-bottom: 8px;
                }
                .asset-list {
                    border: 1px solid #d9d9d9;
                    border-radius: 4px;
                    padding: 8px;
                    overflow-y: auto;
                    max-height: 720px;
                }
                .asset-item {
                    display: flex;
                    align-items: center;
                    margin-bottom: 8px;
                }
                .asset-card {
                    width: 100%;
                }
                .asset-info {
                    display: flex;
                    justify-content: space-between;
                    align-items: center;
                    gap: 8px;
                }
                .asset-id, .asset-name, .asset-type {
                    font-size: 12px;
                }
                .asset-name {
                    width: 100%;
                    margin-left: 10px;
                }
                .draggable {
                    cursor: move;
                }
                .drag-handle {
                    margin-right: 8px;
                    color: #999;
                }
            `}</style>
        </div>
    );
};

export default AssetSelector;
